import React, { useState, useEffect } from 'react';
import { searchSummary } from '../common/form/MobileFilterCollapse';
import SearchCard, { fieldProps } from '../common/list/SearchCard';
import { Box, TextField, MenuItem, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  firstDayofYear,
  getFirstDayOfYear,
  getLastDayOfYear,
  lastDayofYear,
  subYearStrDt,
} from '../../utils/calcDatetime';
import 'dayjs/locale/ko';
import dayjs from 'dayjs';
import SearchCodeSelectbox from '../common/form/selectbox/SearchCodeSelectbox';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MobileSearchAdornment from '../common/search/MobileSearchAdornment';

// 계약 관리(/projectmng) 검색 영역 - chancemng 설계(fc30533) 적용(2026-10-22).
export const ProjectListBox = ({ formik, onReset }) => {
  const [searchStrtDt, setSearchStrtDt] = useState(null);
  const [searchEndDt, setSearchEndDt] = useState(null);

  useEffect(() => {
    if (formik.values.searchStrtDt) {
      setSearchStrtDt(dayjs(formik.values.searchStrtDt));
    }
    if (formik.values.searchEndDt) {
      setSearchEndDt(dayjs(formik.values.searchEndDt));
    }
  }, [formik.values?.searchStrtDt, formik.values?.searchEndDt]);

  const onChangeDate = (newValue, name) => {
    formik.setFieldValue(name, newValue);
  };

  const handleChangeThisYear = () => {
    formik.setFieldValue('searchStrtDt', firstDayofYear);
    formik.setFieldValue('searchEndDt', lastDayofYear);
  };
  const handleChangeLastYear = () => {
    const paramDate = subYearStrDt(1);
    const strDt = getFirstDayOfYear(paramDate);
    const endDt = getLastDayOfYear(paramDate);

    formik.setFieldValue('searchStrtDt', strDt);
    formik.setFieldValue('searchEndDt', endDt);
  };

  return (
    <SearchCard
      summary={searchSummary(formik.values)}
      onSubmit={formik.handleSubmit}
      onReset={onReset}
      submitting={formik.isSubmitting}
      gridSx={{
        gridTemplateAreas: {
          xs: `"period" "impt" "keyword" "search" "btns"`,
          sm: `"period impt keyword" "search search btns"`,
        },
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'minmax(240px, 1.2fr) minmax(130px, 0.7fr) minmax(130px, 0.7fr)',
        },
        '@media (min-width:1440px)': {
          gridTemplateAreas: `"period impt keyword search btns"`,
          gridTemplateColumns:
            'minmax(280px, 1.3fr) minmax(120px, 0.5fr) minmax(130px, 0.6fr) minmax(150px, 1fr) 190px',
        },
      }}
    >
      {/* 기간: 금년/작년 토글 + 시작~종료 */}
      <Box
        sx={{
          gridArea: 'period',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        <ToggleButtonGroup size="small" exclusive sx={{ flexShrink: 0, flexWrap: 'nowrap' }}>
          <ToggleButton value="thisYear" onClick={handleChangeThisYear} sx={{ textTransform: 'none', px: 1.25 }}>
            금년
          </ToggleButton>
          <ToggleButton value="lastYear" onClick={handleChangeLastYear} sx={{ textTransform: 'none', px: 1.25 }}>
            작년
          </ToggleButton>
        </ToggleButtonGroup>

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
            <DatePicker
              label="매출 시작일"
              format="YYYY-MM-DD"
              mask={'____-__-__'}
              id="searchStrtDt"
              name="searchStrtDt"
              value={searchStrtDt}
              onChange={(newValue) => {
                setSearchStrtDt(dayjs(newValue));
                onChangeDate(dayjs(newValue), 'searchStrtDt');
              }}
              slotProps={{
                textField: {
                  ...fieldProps,
                  sx: { minWidth: 0, flex: '1 1 130px' },
                  error: formik.touched.searchStrtDt && Boolean(formik.errors.searchStrtDt),
                  helperText: formik.touched.searchStrtDt && formik.errors.searchStrtDt,
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              ~
            </Typography>
            <DatePicker
              label="매출 종료일"
              format="YYYY-MM-DD"
              mask={'____-__-__'}
              id="searchEndDt"
              name="searchEndDt"
              value={searchEndDt}
              onChange={(newValue) => {
                setSearchEndDt(dayjs(newValue));
                onChangeDate(dayjs(newValue), 'searchEndDt');
              }}
              slotProps={{
                textField: {
                  ...fieldProps,
                  sx: { minWidth: 0, flex: '1 1 130px' },
                  error: formik.touched.searchEndDt && Boolean(formik.errors.searchEndDt),
                  helperText: formik.touched.searchEndDt && formik.errors.searchEndDt,
                },
              }}
            />
          </Box>
        </LocalizationProvider>
      </Box>

      {/* 프로젝트 중요도 */}
      <Box sx={{ gridArea: 'impt', minWidth: 0 }}>
        <SearchCodeSelectbox
          formik={formik}
          variant="outlined"
          size="small"
          name="searchPrjImpt"
          label="프로젝트 중요도"
          required={false}
          filterObject={{
            blcCd: 'BASE',
            mlcCd: 'FORCAST',
            slcCd: 'PRJ_IMPT',
          }}
        />
      </Box>

      {/* 검색키워드 */}
      <Box sx={{ gridArea: 'keyword', minWidth: 0 }}>
        <TextField
          {...fieldProps}
          select
          fullWidth
          required={false}
          id="searchKeyword"
          name="searchKeyword"
          label="검색키워드"
          value={formik.values.searchKeyword}
          onChange={formik.handleChange}
          error={formik.touched.searchKeyword && Boolean(formik.errors.searchKeyword)}
          helperText={formik.touched.searchKeyword && formik.errors.searchKeyword}
        >
          <MenuItem value="prjNo">프로젝트 번호</MenuItem>
          <MenuItem value="cstNm">사업명</MenuItem>
          <MenuItem value="vendorCmpNm">매출처명</MenuItem>
          <MenuItem value="crtNm">영업담당자</MenuItem>
        </TextField>
      </Box>

      {/* 검색어 */}
      <Box sx={{ gridArea: 'search', minWidth: 0 }}>
        <TextField
          {...fieldProps}
          fullWidth
          required={false}
          id="searchWords"
          name="searchWords"
          label="검색어"
          value={formik.values.searchWords}
          onChange={formik.handleChange}
          error={formik.touched.searchWords && Boolean(formik.errors.searchWords)}
          helperText={formik.touched.searchWords && formik.errors.searchWords}
          InputProps={{
            endAdornment: <MobileSearchAdornment disabled={formik.isSubmitting} />,
          }}
        />
      </Box>
    </SearchCard>
  );
};
